import PartyName from 'components/party/PartyName';
import { useIntl } from 'react-intl';
import { ClassifierData } from 'types/classifiers';
import { ClientIdentificationPanelDto } from 'types/clientIdentification';
import { PartyType } from 'types/contacts';
import { getClassifierText, t } from 'utils/getTranslations';
import { itemValue, useIdentityValueContext } from '../identityValues';
import { isRequiredClientItem, itemLabel } from '../missingItems';
import { MissingChip, Value, VALUE_GRID_CLASS } from './Block';
import { ClientParty } from './ClientAndTierBlock';

/** The client items the client identification requires of every client; the firm's switches do not change them. */
const WIDE_CLIENT_ITEMS = new Set([
    'REGISTRATION_ADDRESS',
    'HOME_ADDRESS',
    'PRINCIPAL_PLACE_OF_BUSINESS',
    'NATURE_OF_BUSINESS',
]);

export const pepQuestionId = (partyType: PartyType) =>
    partyType === PartyType.COMPANY
        ? 'client-identification.pep-question-company'
        : 'client-identification.pep-question-person';

export function sourceOfFundsNames(codes: string[], classifier: ClassifierData | undefined): string[] {
    return codes.map((code) => getClassifierText(classifier, code) || code);
}

interface Props {
    client: ClientParty;
    panel: ClientIdentificationPanelDto;
    classifiers: Record<string, ClassifierData>;
}

export function ClientDetailsBlock({ client, panel, classifiers }: Props) {
    const intl = useIntl();
    const context = useIdentityValueContext(classifiers);
    const { clientFields, clientPartyType, sourceOfFunds, pep } = panel;
    const employmentStatus = clientFields.employmentStatusCode
        ? getClassifierText(classifiers['EMPLOYMENT_STATUS'], clientFields.employmentStatusCode) ||
          clientFields.employmentStatusCode
        : null;
    return (
        <>
            <PartyName party={client} renderPartyIcon variant="readonly-other" className="font-semibold" />
            <div className={VALUE_GRID_CLASS}>
                {panel.clientItems.map((item) => (
                    <Value
                        key={item.key}
                        label={itemLabel(item.key)}
                        required={isRequiredClientItem(item.key, panel.tierCode)}
                        missing={item.isMissing}
                        wide={WIDE_CLIENT_ITEMS.has(item.key)}
                        detail={
                            item.key === 'OCCUPATION'
                                ? [
                                      employmentStatus
                                          ? t('client-identification.employment-status-line', {
                                                status: employmentStatus,
                                            })
                                          : null,
                                      panel.employers.length
                                          ? t('client-identification.employers-line', {
                                                employers: intl.formatList(panel.employers, { type: 'unit' }),
                                            })
                                          : null,
                                  ]
                                      .filter(Boolean)
                                      .join(' · ') || undefined
                                : undefined
                        }
                    >
                        {itemValue(item.key, clientFields, clientPartyType, context)}
                    </Value>
                ))}
                {panel.asksSourceOfFunds ? (
                    <Value
                        label={t('client-identification.item.SOURCE_OF_FUNDS')}
                        required
                        missing={sourceOfFunds.isMissing}
                        wide
                        detail={
                            sourceOfFunds.relevantBanks
                                ? t('client-identification.relevant-banks-line', { banks: sourceOfFunds.relevantBanks })
                                : undefined
                        }
                    >
                        {sourceOfFunds.codes.length ? (
                            <>
                                {[
                                    intl.formatList(
                                        sourceOfFundsNames(sourceOfFunds.codes, classifiers['SOURCE_OF_FUNDS']),
                                        { type: 'unit' }
                                    ),
                                ]
                                    .concat(sourceOfFunds.note ? [sourceOfFunds.note] : [])
                                    .join(' · ')}
                                {sourceOfFunds.isNoteMissing ? (
                                    <>
                                        {' '}
                                        <MissingChip label={t('client-identification.note-missing')} />
                                    </>
                                ) : null}
                            </>
                        ) : null}
                    </Value>
                ) : null}
                {panel.asksPep ? (
                    <Value
                        label={t('client-identification.item.PEP')}
                        description={t(pepQuestionId(clientPartyType))}
                        required
                        missing={pep.isMissing && !pep.answer}
                        wide
                        detail={pep.answer === 'YES' ? pep.details : undefined}
                    >
                        {pep.answer ? t(pep.answer === 'YES' ? 'yes' : 'no') : null}
                        {pep.isMissing && pep.answer ? (
                            <>
                                {' '}
                                <MissingChip label={t('client-identification.details-missing')} />
                            </>
                        ) : null}
                    </Value>
                ) : null}
            </div>
        </>
    );
}
